import { useI18n } from '@/context/I18nProvider';
import { Check } from '@/components/ui/icons/Icons';

interface PasswordRule {
  key:
    | 'auth.passwordRules.minLength'
    | 'auth.passwordRules.lowercase'
    | 'auth.passwordRules.uppercase'
    | 'auth.passwordRules.digit'
    | 'auth.passwordRules.special';
  met: boolean;
}

function getPasswordRules(password: string): PasswordRule[] {
  return [
    { key: 'auth.passwordRules.minLength', met: password.length >= 8 },
    { key: 'auth.passwordRules.lowercase', met: /[a-z]/.test(password) },
    { key: 'auth.passwordRules.uppercase', met: /[A-Z]/.test(password) },
    { key: 'auth.passwordRules.digit', met: /\d/.test(password) },
    { key: 'auth.passwordRules.special', met: /[^a-zA-Z0-9]/.test(password) },
  ];
}

function getStrength(rulesMet: number): {
  level: 'weak' | 'normal' | 'strong';
  key:
    | 'auth.passwordStrength.weak'
    | 'auth.passwordStrength.normal'
    | 'auth.passwordStrength.strong';
  color: string;
  barColor: string;
  segments: number;
} {
  if (rulesMet <= 2) {
    return {
      level: 'weak',
      key: 'auth.passwordStrength.weak',
      color: 'text-red-500',
      barColor: 'bg-red-500',
      segments: 1,
    };
  }
  if (rulesMet <= 4) {
    return {
      level: 'normal',
      key: 'auth.passwordStrength.normal',
      color: 'text-yellow-500',
      barColor: 'bg-yellow-500',
      segments: 2,
    };
  }
  return {
    level: 'strong',
    key: 'auth.passwordStrength.strong',
    color: 'text-green-500',
    barColor: 'bg-green-500',
    segments: 3,
  };
}

interface PasswordStrengthGaugeProps {
  password: string;
}

const PasswordStrengthGauge = ({ password }: PasswordStrengthGaugeProps) => {
  const { t } = useI18n();

  if (!password) return null;

  const rules = getPasswordRules(password);
  const rulesMet = rules.filter((r) => r.met).length;
  const strength = getStrength(rulesMet);

  return (
    <div className="mt-2 space-y-2">
      {/* Strength bar */}
      <div className="flex items-center gap-2">
        <div className="flex flex-1 gap-1">
          {[1, 2, 3].map((segment) => (
            <div
              key={segment}
              className={`h-1.5 flex-1 rounded-full transition-colors ${
                segment <= strength.segments ? strength.barColor : 'bg-gray-200'
              }`}
            />
          ))}
        </div>
        <span className={`text-xs font-medium ${strength.color}`}>{t(strength.key)}</span>
      </div>

      {/* Rules checklist */}
      <ul className="space-y-0.5">
        {rules.map((rule) => (
          <li
            key={rule.key}
            className={`flex items-center gap-1.5 text-xs ${
              rule.met ? 'text-green-600' : 'text-muted-foreground'
            }`}
          >
            {rule.met ? (
              <Check className="size-3 shrink-0" strokeWidth={3} />
            ) : (
              <span className="size-3 shrink-0 flex items-center justify-center">○</span>
            )}
            <span>{t(rule.key)}</span>
          </li>
        ))}
      </ul>
    </div>
  );
};

export { PasswordStrengthGauge, getPasswordRules };
